import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SITE_NAME = 'IURIX';
export const SITE_URL = 'https://www.iurix.com';
export const DEFAULT_DESCRIPTION =
  'IURIX delivers expert IT consulting, cloud infrastructure, DevOps automation, AI & analytics, and cybersecurity services for enterprises worldwide.';
export const DEFAULT_IMAGE = `${SITE_URL}/social-share.jpg`;

/**
 * SEO
 * Centralized, per-page metadata (title, description, canonical, Open Graph,
 * Twitter Card) plus optional JSON-LD structured data.
 *
 * Usage:
 *   <SEO
 *     title="Our Services"
 *     description="..."
 *     path="/services"
 *   />
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = null,
  lang,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — IT Consulting & Digital Transformation`;
  const canonicalUrl = `${SITE_URL}${path === '/' ? '' : path}`;
  const jsonLdList = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      {lang && <html lang={lang} />}
      <title>{fullTitle}</title>
      <meta name='description' content={description} />
      <link rel='canonical' href={canonicalUrl} />
      {noindex && <meta name='robots' content='noindex, nofollow' />}

      {/* Open Graph */}
      <meta property='og:type' content='website' />
      <meta property='og:site_name' content={SITE_NAME} />
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={description} />
      <meta property='og:url' content={canonicalUrl} />
      <meta property='og:image' content={image} />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={image} />

      {jsonLdList.map((schema, idx) => (
        <script key={idx} type='application/ld+json'>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

/** Reusable Organization schema for JSON-LD (used on the homepage). */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: DEFAULT_DESCRIPTION,
  sameAs: [
    'https://www.facebook.com/iurixtech',
    'https://www.instagram.com/iurixtech/',
    'https://www.youtube.com/user/TheIurix',
    'https://twitter.com/iurixcom',
    'https://www.linkedin.com/in/iuridearaujo/',
  ],
};
